import * as stylex from "@stylexjs/stylex";
import { useEffect, useState } from "react";
import { useMutation, useSuspenseQuery } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import PreviewCanvas from "@/components/review/PreviewCanvas";
import PublicCommentsPanel from "@/components/review/PublicCommentsPanel";
import RecordingViewer from "@/components/review/RecordingViewer";
import WebsitePreview from "@/components/review/WebsitePreview";
import type { PreviewPinDisplay } from "@/components/review/PinLayer";
import { commentAnchor, describeMutationError, pinNumbersForVersion, type CommentAnchor } from "@/components/review/review-model";
import Button from "@/components/ui/Button";
import EmptyState from "@/components/ui/EmptyState";
import Field from "@/components/ui/Field";
import Input from "@/components/ui/Input";
import MenuSelect from "@/components/ui/MenuSelect";
import { recipes } from "@/design/recipes";
import "@/design/tokens.stylex";
import { colors, layout, radii, shadows, space, text } from "@/design/tokens.stylex";
import { showError } from "@/lib/toast";
import { useDocumentTitle } from "@/lib/window-title";
import { queryClient, trpc } from "@/lib/trpc";
const NARROW = "@media (max-width: 1023px)";
const styles = {
  wide: {
    kzqmXN: "xh8yej3",
    $$css: true
  },
  title: {
    kogj98: "x1ghz6dp",
    kGuDYH: "xxmgfec",
    k63SB2: "xk50ysn",
    kMwMTN: "xgwkh9f",
    $$css: true
  },
  source: {
    ks0D6T: "x1n8psvb",
    kMwMTN: "x1e3avs9",
    $$css: true
  }
};
export const Route = createFileRoute("/review/$draftId")({
  loader: ({
    params,
    context
  }) => context.queryClient.ensureQueryData(trpc.review.getPublicDraft.queryOptions({
    draftId: params.draftId
  })),
  component: PublicReviewPage
});
function PublicReviewPage() {
  const {
    draftId
  } = Route.useParams();
  const {
    data
  } = useSuspenseQuery(trpc.review.getPublicDraft.queryOptions({
    draftId
  }));
  const {
    draft,
    versions,
    comments
  } = data;
  const [guestName, setGuestName] = useState("");
  const [nameInput, setNameInput] = useState("");
  const [selectedVersionId, setSelectedVersionId] = useState<string | null>(null);
  const [pendingAnchor, setPendingAnchor] = useState<CommentAnchor | null>(null);
  const [pendingCrop, setPendingCrop] = useState<string | null>(null);
  const [selectedCommentId, setSelectedCommentId] = useState<string | null>(null);
  const [videoTime, setVideoTime] = useState<number | null>(null);
  const addMutation = useMutation(trpc.review.addPublicComment.mutationOptions());
  // A guest is outside any Company, and the shared review does not name one.
  useDocumentTitle({
    section: "Review",
    detail: draft.title
  });
  useEffect(() => {
    const saved = window.sessionStorage.getItem("review-guest-name") ?? "";
    setGuestName(saved);
    setNameInput(saved);
  }, []);
  const selectedVersion = versions.find(version => version.id === selectedVersionId) ?? versions[0] ?? null;
  const versionComments = selectedVersion ? comments.filter(comment => comment.versionId === selectedVersion.id) : [];
  const pinNumbers = pinNumbersForVersion(versionComments);
  const selectedComment = versionComments.find(comment => comment.id === selectedCommentId);
  const pins: PreviewPinDisplay[] = versionComments.flatMap(comment => {
    const anchor = commentAnchor(comment);
    const number = pinNumbers.get(comment.id);
    return anchor && number != null ? [{
      commentId: comment.id,
      number,
      anchor,
      resolved: comment.status === "resolved",
      selected: selectedCommentId === comment.id
    }] : [];
  });
  async function submitComment(body: string, parentId?: string, anchor?: CommentAnchor) {
    if (!selectedVersion || !guestName) return;
    try {
      await addMutation.mutateAsync({
        draftId,
        versionId: selectedVersion.id,
        guestName,
        body,
        parentId,
        anchor,
        crop: anchor?.pageId && anchor === pendingAnchor ? pendingCrop ?? undefined : undefined
      });
      setPendingAnchor(null);
      setPendingCrop(null);
      await queryClient.invalidateQueries({
        queryKey: trpc.review.getPublicDraft.queryKey({
          draftId
        })
      });
    } catch (cause) {
      showError(describeMutationError(cause).message);
      throw cause;
    }
  }
  if (!guestName) {
    return <main className="xrvj5dj x1ku5rj1 xg6iff7 xze2s60 x19qxo9d">
        <form className="x78zum5 xdt5ytf x1l51sop xh8yej3 x1j9u4d2 xze2s60 x1h1f4i9 xmkeg23 x1y0btm7 xzbo3x7 xhrzdqn x1dgl1gp" onSubmit={event => {
        event.preventDefault();
        const next = nameInput.trim();
        if (!next) return;
        window.sessionStorage.setItem("review-guest-name", next);
        setGuestName(next);
      }}>
          <div className="x78zum5 xdt5ytf xsuxsxl">
            <span className="x7x6yu3 x1i6qc9y">Shared review</span>
            <h1 className="x1ghz6dp xuxpmqx xk50ysn xjat59b xgwkh9f xj0a0fe">{draft.title}</h1>
          </div>
          <Field label="What should we call you?" htmlFor="guest-name">
            <Input id="guest-name" autoFocus value={nameInput} onChange={event => setNameInput(event.target.value)} placeholder="Your name" maxLength={80} />
          </Field>
          <Button type="submit" variant="primary" xstyle={styles.wide} disabled={!nameInput.trim()}>
            Open review
          </Button>
        </form>
      </main>;
  }
  const recording = selectedVersion?.recording ?? null;
  const clearPending = () => {
    setPendingAnchor(null);
    setPendingCrop(null);
  };
  const webPreviewUrl = draft.kind === "web" && selectedVersion?.mimeType === "text/html" ? selectedVersion.pageUrl ?? draft.sourceUrl : null;
  return <div className="x78zum5 xdt5ytf x1dr59a3 x2lwn1j x1h1f4i9">
      <header className="x78zum5 x6s0dn4 x1qughib x1l51sop x1pruydp xj9id08 xf8hsz2 xso031l x1q0q8m5 xaecqrk">
        <div className="x78zum5 xdt5ytf x3d5knr xeuugli">
          <h1 {...stylex.props(styles.title, recipes.truncate)}>{draft.title}</h1>
          <div className="x78zum5 x1a02dak x6s0dn4 x1p65qpe xeuugli x7x6yu3 x1i6qc9y">
            <span>Commenting as {guestName}</span>
            {/* A recording names its page in the viewer. */}
            {selectedVersion && !recording && <>
                <span aria-hidden>·</span>
                <span {...stylex.props(styles.source, recipes.truncate)}>
                  {draft.kind === "web" && (selectedVersion.pageUrl ?? draft.sourceUrl) ? websiteCaptureLabel(selectedVersion.pageUrl ?? draft.sourceUrl, selectedVersion.versionNumber).replace(/ · capture \d+$/, "") : selectedVersion.fileName}
                </span>
              </>}
          </div>
        </div>
        <Button variant="ghost" size="sm" onClick={() => {
        window.sessionStorage.removeItem("review-guest-name");
        setGuestName("");
      }}>
          Change name
        </Button>
      </header>
      <div className="x78zum5 x1q0g3np x1dh0ydc x98rzlu x2lwn1j xb3r6kr xcay4t3">
        <div {...{
        0: {
          className: "x98rzlu x15f271x xeuugli x2lwn1j x913imy xysyzu8 xze2s60 x19qxo9d"
        },
        1: {
          className: "x98rzlu x15f271x xeuugli x2lwn1j x913imy xysyzu8 x19qxo9d x1717udv"
        }
      }[!!Boolean(webPreviewUrl || recording) << 0]}>
          {recording ? <RecordingViewer recording={recording} title={draft.title} pins={pins} pendingAnchor={pendingAnchor} canComment={draft.status !== "archived"} onPickElement={(anchor, crop) => {
          setPendingAnchor(anchor);
          setPendingCrop(crop);
          setSelectedCommentId(null);
        }} onClearPendingAnchor={clearPending} onSelectPin={commentId => setSelectedCommentId(current => current === commentId ? null : commentId)} /> : webPreviewUrl ? <WebsitePreview url={webPreviewUrl} title={draft.title} pins={pins} pendingAnchor={pendingAnchor} canComment={draft.status !== "archived"} onPlacePin={anchor => {
          setPendingAnchor(anchor);
          setSelectedCommentId(null);
        }} onClearPendingAnchor={() => setPendingAnchor(null)} onSelectPin={setSelectedCommentId} /> : selectedVersion ? <div className="x36rmqc xvueqy4">
              <PreviewCanvas version={selectedVersion} pins={pins} pendingAnchor={pendingAnchor} canComment={draft.status !== "archived"} onPlacePin={anchor => {
            setPendingAnchor({
              ...anchor,
              url: draft.kind === "web" ? selectedVersion.pageUrl ?? draft.sourceUrl ?? undefined : undefined
            });
            setSelectedCommentId(null);
          }} onSelectPin={setSelectedCommentId} seekTime={selectedComment ? commentAnchor(selectedComment)?.timecode ?? null : null} onVideoTimeChange={setVideoTime} defaultScale={draft.kind === "web" ? "width" : "fit"} />
            </div> : <EmptyState centered title="No preview attached" />}
        </div>
        <aside aria-label="Comments" className="x78zum5 xdt5ytf x2lah0s x1cy8gbr x14shcbm x2lwn1j x1go5leb x1h1f4i9 xe0pwq x1c1f6qm x19ypqd9 x1ffzt9r x972fbf xb0fqrj x13fuv20 x13hb0vi">
          <div className="x78zum5 xdt5ytf x166cprz x9end0a x1jbum4n xso031l x1q0q8m5 xaecqrk">
            <span className="x7x6yu3 xk50ysn x1i6qc9y">
              {draft.kind === "web" && !recording ? "Page capture" : "Version"}
            </span>
            <MenuSelect value={selectedVersion?.id ?? ""} onValueChange={value => {
            setSelectedVersionId(value);
            clearPending();
            setSelectedCommentId(null);
          }} ariaLabel="Version" fullWidth options={versions.map(version => ({
            value: version.id,
            label: version.recording ? `Version ${version.versionNumber} — ${version.fileName}` : draft.kind === "web" ? websiteCaptureLabel(version.pageUrl, version.versionNumber) : `Version ${version.versionNumber} — ${version.fileName}`
          }))} />
          </div>
          <PublicCommentsPanel comments={versionComments} pinNumbers={pinNumbers} selectedCommentId={selectedCommentId} onSelectComment={setSelectedCommentId} onSubmit={submitComment} pending={addMutation.isPending} pendingAnchor={pendingAnchor} onClearPendingAnchor={clearPending} automaticTimecode={selectedVersion?.mimeType.startsWith("video/") ? videoTime : null} />
        </aside>
      </div>
    </div>;
}
function websiteCaptureLabel(pageUrl: string | null, versionNumber: number) {
  if (!pageUrl) return `Capture ${versionNumber}`;
  try {
    const url = new URL(pageUrl);
    const path = url.pathname === "/" ? "" : url.pathname.replace(/\/$/, "");
    return `${url.hostname.replace(/^www\./, "")}${path} · capture ${versionNumber}`;
  } catch {
    return `Capture ${versionNumber}`;
  }
}